import { CSSProperties, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';

import { CreditCardOutlined as CreditCardIcon } from '@mui/icons-material';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import {
  Box,
  Button,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  SxProps,
  Theme,
} from '@mui/material';
import { collection, onSnapshot, or, query, where } from 'firebase/firestore';
import { BarChart2, MessageCircleQuestion } from 'lucide-react';

import { useFeatureFlags, useLanguage, usePermissionFlags } from '../../hooks';
import {
  AlertTriangleOutline,
  BookOpenOutline,
  ChartLine,
  ChevronLeftOutline,
  ChevronRightOutline,
  Datascope,
  DatascopeSmall,
  Edit2Outline,
  FileOutline,
  GlobeOutline,
  HomeOutline,
  LinkOutline,
  ListChecks,
  ShakeOutline,
} from '../../icons';
import { BellOutline } from '../../icons/BellOutline';
import { FlagOutline } from '../../icons/FlagOutline';
import { QuestionMarkCircleOutline } from '../../icons/QuestionMarkCircleOutline';
import { StarOutline } from '../../icons/StarOutline';
import { useFirebaseStore } from '../../newStore/firebaseStore';
import { useMainLayoutStore } from '../../newStore/mainLayoutStore';
import { useUserDataStore } from '../../newStore/userDataStore';
import { usePendingSignaturesCountQuery } from '../../queries/signature';
import { commonStyles } from '../../style/CommonStyles';
import { paths } from '../../utils/navigation';
import { isSupportOrAdmin } from '../../utils/utils';
import { TruckIcon } from '../icons/truckIcon';
import { MainMenuItem, MenuItemData } from './MainMenuItem';
import { SettingsMenu } from './SettingsMenu';

export const customStyles: Record<string, SxProps<Theme>> = {
  root: {
    position: 'relative',
  },
  rootMenuPaper: {
    color: '#FFFFFF',
    boxSizing: 'border-box',
    background: (theme) => theme.palette.primary.main,
    transition: 'width 0.3s ease-in-out',
    borderRight: 'unset',
    padding: '5px 12px 24px 12px',
    zIndex: 1300, // Some MUI components are at ~1200
  },
  mobileMenu: {
    display: { xs: 'block', md: 'none' },
    transition: 'width 0.3s ease-in-out',
    zIndex: 1300,
  },
  desktopMenu: {
    display: { xs: 'none', md: 'block' },
    transition: 'width 0.3s ease-in-out',
    flexShrink: 0,
    zIndex: 1300,
  },
  logoContainer: {
    height: '60px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuItemList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  menuItem: {
    padding: '6px 12px',
    height: '34px',
    boxSizing: 'border-box',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    '&:hover': {
      backgroundColor: '#6DB1FB',
    },
    '& .MuiListItemIcon-root': {
      color: 'inherit',
      minWidth: 'unset',
      '& svg': {
        height: '20px',
        width: '20px',
      },
    },
  },
  unexpandedDesktopItem: {
    display: 'none',
  },
  expandMenuContainer: {
    position: 'absolute',
    right: '-14px',
    top: '-2px',
    bottom: '0px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  fullLogo: {
    width: '151px',
    height: 'auto',
  },
  expandMenuButton: {
    display: { xs: 'none', md: 'flex' },
    minWidth: '14px',
    width: '14px',
    height: '66px',
    padding: 0,
    borderRadius: '0px',
    color: '#FFFFFF',
    clipPath: 'polygon(0 0, 100% 12%, 100% 88%, 0% 100%)',
    backgroundColor: (theme) => theme.palette.primary.main,
    '&:hover': {
      backgroundColor: '#6DB1FB',
    },
    zIndex: 1300,
  },
  divider: {
    backgroundColor: '#FFFFFF',
    margin: '6px 4px',
  },
  notification: {
    backgroundColor: '#FA8C16',
    borderRadius: '50%',
    width: '18px',
    height: '18px',
    position: 'absolute',
    textAlign: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    top: '-2px',
    right: '-19px',
    fontSize: '12px',
    fontWeight: 600,
  },
  notificationCollapsed: {
    backgroundColor: '#FA8C16',
    borderRadius: '50%',
    width: '18px',
    height: '18px',
    position: 'absolute',
    textAlign: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    top: '-8px',
    right: '-8px',
    fontSize: '12px',
    fontWeight: 600,
  },
  new: {
    backgroundColor: '#D8EFFE',
    borderRadius: '10px',
    padding: '4px 8px',
    position: 'absolute',
    right: '4px',
    '&.MuiTypography-root': {
      fontSize: '11px',
      lineHeight: '11px',
      fontWeight: 500,
      color: '#1F52B3',
    },
  },
  menuItemText: {
    position: 'relative',
    width: 'fit-content',
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: 400,
    whiteSpace: 'nowrap',
  },
  newMenuItem: {
    width: 'auto',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
} satisfies Record<string, SxProps<Theme>>;

export function MainMenu() {
  const lang = useLanguage();
  const currentUser = useUserDataStore((state) => state.currentUser);
  const firestore = useFirebaseStore((state) => state.firestore);
  const firebaseConfigured = useFirebaseStore((state) => state.firebaseConfigured);
  const user = useFirebaseStore((state) => state.user);
  const helpCenterUrl = useUserDataStore((state) => state.currentUser?.help_center_url);
  const hasFullAccess = isSupportOrAdmin(currentUser) || currentUser?.is_admin;

  const { openMobile, setOpenMobile, expandedDesktop, setExpandedDesktop } = useMainLayoutStore();
  const { checkFlag } = useFeatureFlags();
  const { checkPermission } = usePermissionFlags();
  const [findingsNotification, setFindingsNotification] = useState<number | null>(null);

  const { data: pendingSignaturesCount } = usePendingSignaturesCountQuery();

  const drawerWidth = expandedDesktop ? 230 : 72;
  const fullAccess = currentUser?.is_superadmin || currentUser?.is_admin || currentUser?.is_support;

  useEffect(() => {
    if (!firebaseConfigured || !currentUser) return;

    const findingsQuery = query(
      collection(firestore, `datascope/${user.uid}/findings`),
      !(fullAccess || checkPermission('admin_findings')) &&
        or(
          where('creator.user_id', '==', currentUser.id),
          where('assignees', 'array-contains', currentUser.id),
          where('invitees', 'array-contains', currentUser.id),
        ),
    );

    const unsubFindings = onSnapshot(findingsQuery, (querySnapshot) => {
      const findings = querySnapshot.docs.map((doc) => doc.data());
      const findingsNotification = findings.filter(
        (finding) => finding.status !== 'closed' && !finding.deleted,
      ).length;
      setFindingsNotification(findingsNotification);
    });

    return () => unsubFindings();
  }, [firebaseConfigured, currentUser]);

  const menuItemsBase: MenuItemData[] = [
    {
      label: lang.mainMenu.home,
      icon: <HomeOutline />,
      path: paths.home.home,
    },
    {
      label: lang.mainMenu.charts,
      icon: <ChartLine />,
      path: paths.charts.index,
      forceRerender: true,
      hide: !fullAccess && !checkPermission('access_to_charts'),
    },
    {
      label: lang.mainMenu.taskAssigner,
      icon: <ListChecks />,
      path: paths.taskAssigner.index,
      hide: !fullAccess && !checkPermission('assigned'),
    },
    {
      label: lang.mainMenu.map,
      icon: <GlobeOutline />,
      path: paths.map.index,
      forceRerender: true,
      hide: !fullAccess && !checkPermission('answers'),
    },
    {
      label: lang.mainMenu.settings.changelog,
      icon: <BellOutline />,
      path: paths.changeLog.index,
      hide: !fullAccess && (!checkPermission('users') || !checkPermission('metadata')),
    },
    {
      label: lang.mainMenu.forms,
      icon: <FileOutline />,
      path: paths.forms.index,
      hide: !(
        fullAccess ||
        checkPermission('metadata') ||
        checkPermission('edit_forms') ||
        (checkFlag('RegisteredWebforms') && checkPermission('tasks'))
      ),
    },
    {
      label: lang.mainMenu.findings,
      icon: <AlertTriangleOutline />,
      path: paths.findings.index,
      notification: findingsNotification,
    },
    {
      label: lang.mainMenu.signatures,
      icon: <Edit2Outline />,
      path: `${paths.signature.index}?source=from_main_menu`,
      notification: pendingSignaturesCount,
    },
    {
      label: lang.mainMenu.assets,
      icon: <TruckIcon />,
      path: paths.assets.types,
      hide: (!checkPermission('admin_assets') && !hasFullAccess) || !checkFlag('Assets'),
    },
    {
      label: lang.mainMenu.integrations,
      icon: <LinkOutline />,
      path: paths.integrations.index,
      forceRerender: true,
      hide: !fullAccess && !checkPermission('export'),
    },
    {
      label: lang.mainMenu.locations,
      icon: <PlaceOutlinedIcon />,
      path: paths.locations.index,
      hide: !checkFlag('NestableLocations'),
      new: true,
    },
    {
      label: lang.mainMenu.askDataScope || 'Ask DataScope',
      icon: <MessageCircleQuestion size={20} />,
      path: paths.askDataScope.index,
      hide: !checkFlag('AskDataScope'),
      new: true,
    },
    {
      label: lang.mainMenu.myDashboards || 'My Dashboards',
      icon: <BarChart2 size={20} />,
      path: paths.dashboards.index,
      hide: !checkFlag('AskDataScope'),
    },
  ];

  const menuItemsExtra: MenuItemData[] = [
    {
      label: lang.mainMenu.sensors,
      icon: <ShakeOutline />,
      path: paths.sensors.index,
      hide: !checkFlag('labsSensor'),
      forceRerender: true,
    },
    {
      label: lang.mainMenu.training,
      icon: <BookOpenOutline />,
      path: paths.training.index,
      hide: !checkFlag('labsTraining'),
      forceRerender: true,
    },
  ];

  const paymentsV2Enabled = checkFlag('PaymentsV2');

  const menuContent = (
    <Box>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Box sx={customStyles.logoContainer}>
          <NavLink
            to='/'
            style={commonStyles.linkOverride as CSSProperties}
          >
            {expandedDesktop ? (
              <Datascope sx={customStyles.fullLogo} />
            ) : (
              <DatascopeSmall fontSize='large' />
            )}
          </NavLink>
        </Box>
        <List
          component='nav'
          sx={customStyles.menuItemList}
        >
          {menuItemsBase
            .filter((item) => !item.hide)
            .map((item, index) => (
              <MainMenuItem
                key={item.label + index}
                item={item}
                expandedDesktop={expandedDesktop}
              />
            ))}
          <SettingsMenu key='settingsMenuKey' />
          {menuItemsExtra
            .filter((item) => !item.hide)
            .map((item, index) => (
              <MainMenuItem
                key={item.label + index}
                item={item}
                expandedDesktop={expandedDesktop}
              />
            ))}
          <ListItemButton
            sx={customStyles.menuItem}
            component='a'
            target='_blank'
            href={helpCenterUrl}
          >
            <ListItemIcon>
              <QuestionMarkCircleOutline />
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={customStyles.menuItemText}>{lang.mainMenu.settings.helpCenter}</Box>
              }
              sx={{
                ...(!expandedDesktop && {
                  display: 'none',
                }),
              }}
            />
          </ListItemButton>
          <ListItemButton
            id='datascope-news'
            sx={customStyles.menuItem}
          >
            <ListItemIcon>
              <StarOutline />
            </ListItemIcon>
            <ListItemText
              primary={<Box sx={customStyles.menuItemText}>{lang.mainMenu.settings.news}</Box>}
              sx={{
                ...(!expandedDesktop && {
                  display: 'none',
                }),
              }}
            />
          </ListItemButton>
          {currentUser?.isOpsCenterAdmin && (
            <MainMenuItem
              item={{
                label: 'Feature Flags & DevOps',
                icon: <FlagOutline />,
                path: paths.opsCenter.index,
              }}
              expandedDesktop={expandedDesktop}
            />
          )}
        </List>
      </Box>
      {paymentsV2Enabled && !currentUser?.is_paying && (
        <NavLink
          to={paths.billing.index}
          style={commonStyles.linkOverride as CSSProperties}
        >
          <ListItemButton sx={customStyles.menuItem}>
            <ListItemIcon>
              <CreditCardIcon fontSize='small' />
            </ListItemIcon>
            <ListItemText
              primary={<Box sx={customStyles.menuItemText}>{lang.mainMenu.settings.upgrade}</Box>}
              sx={{
                ...(!expandedDesktop && {
                  display: 'none',
                }),
              }}
            />
          </ListItemButton>
        </NavLink>
      )}
    </Box>
  );
  return (
    <Box sx={customStyles.root}>
      <Drawer
        variant='temporary'
        open={openMobile}
        onClose={() => setOpenMobile(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: drawerWidth,
            ...customStyles.rootMenuPaper,
          },
        }}
        sx={
          {
            width: drawerWidth,
            ...customStyles.mobileMenu,
          } as SxProps<Theme>
        }
      >
        {menuContent}
      </Drawer>
      <Drawer
        variant='permanent'
        sx={
          {
            width: drawerWidth,
            ...customStyles.desktopMenu,
          } as SxProps<Theme>
        }
        PaperProps={{
          sx: {
            width: drawerWidth,
            ...customStyles.rootMenuPaper,
          },
        }}
        open
      >
        {menuContent}
      </Drawer>
      <Box sx={customStyles.expandMenuContainer}>
        <Button
          onClick={() => setExpandedDesktop(!expandedDesktop)}
          sx={customStyles.expandMenuButton}
        >
          {expandedDesktop ? <ChevronLeftOutline /> : <ChevronRightOutline />}
        </Button>
      </Box>
    </Box>
  );
}
